import { useEffect, useState, useCallback } from "react";

import { useNavigate } from "react-router-dom";

import { useAtom, useSetAtom } from "jotai";

import { userAtom } from "@/atoms/auth.atoms";

import { serverHostAtom, serverPortAtom } from "@/atoms/settings.atoms";

import { getGuestDeviceId } from "@/services/signaling.client";

interface PairRequest {
  hostName: string;

  hostIp: string;

  hostPort: number;

  requestId: string;
}

export function useGuestPairing() {
  const navigate = useNavigate();

  const [user] = useAtom(userAtom);

  const [pairRequest, setPairRequest] = useState<PairRequest | null>(null);

  const setServerHost = useSetAtom(serverHostAtom);

  const setServerPort = useSetAtom(serverPortAtom);

  // Publish guest via mDNS when authenticated

  useEffect(() => {
    const api = window.electronAPI;

    if (!api?.publishGuest || !user?.name) return;

    api.publishGuest(user.name);

    return () => {
      api.unpublishGuest?.();
    };
  }, [user?.name]);

  // Listen for incoming pair requests

  useEffect(() => {
    const api = window.electronAPI;

    if (!api?.onPairRequest) return;

    api.onPairRequest((data) => {
      setPairRequest(data);
    });
  }, []);

  const respondToPairRequest = useCallback(
    (requestId: string, accepted: boolean) => {
      window.electronAPI?.respondPairRequest?.(
        requestId,

        accepted,

        getGuestDeviceId(),
      );

      if (accepted && pairRequest) {
        // Auto-set server connection details from the host that paired with us

        const host = pairRequest.hostIp;

        const port = String(pairRequest.hostPort);

        setServerHost(host);

        setServerPort(port);

        localStorage.setItem("@Guest/serverHost", host);

        localStorage.setItem("@Guest/serverPort", port);

        navigate("/home", { state: { fromPairing: true } });
      }

      setPairRequest(null);
    },

    [pairRequest, setServerHost, setServerPort, navigate],
  );

  return { pairRequest, respondToPairRequest };
}
